
import Vue, { PropType } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import moment from 'moment-timezone';
import {
	FeedbackReport,
	FeedbackReportBoard,
	FeedbackReportComment,
	FeedbackReportOrigin,
	FeedbackReportOriginKind,
	FeedbackReportPriority,
	FeedbackReportStatus,
	FEEDBACK_REPORT_PRIORITIES,
	FEEDBACK_REPORT_STATUSES,
	isFeedbackReportClosed,
	User,
} from '@run-diffusion/shared';
import { db, DocumentReference, FieldValue, functions } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import NumberField from '@/components/base/NumberField.vue';
import FeedbackAssignDialog from './FeedbackAssignDialog.vue';
import FeedbackChip from './FeedbackChip.vue';
import FeedbackActivity from './FeedbackActivity.vue';
import FeedbackComments from './FeedbackComments.vue';
import FeedbackShotViewer from './FeedbackShotViewer.vue';
import FeedbackStatusDialog from './FeedbackStatusDialog.vue';
import { feedbackFileUrl } from './feedbackFiles';
import { copyText } from './clipboard';
import { findReportByNumber } from './findReport';
import { FeedbackAdmin } from './feedbackAdmins';
import {
	AgentReportLinks,
	arrival,
	claimBlocked,
	askedDuringWork,
	assignUpdate,
	claimUpdate,
	copiedMessage,
	currentHolder,
	FeedbackFileLinksResult,
	FeedbackUpdate,
	hasBoard,
	hasBuild,
	hasEnv,
	hasViewport,
	heldByOther,
	httpLink,
	isAdminReport,
	isPullRequestUrl,
	KIND_LABELS,
	KIND_QUESTIONS,
	ORIGIN_LABELS,
	originKind,
	PRIORITY_LABELS,
	ReportHolder,
	reportForAgent,
	STATUS_LABELS,
	statusNeedsNote,
	statusComment,
	statusUpdate,
	takeoverOutcomeText,
	triageUpdate,
} from './feedbackReports';

// how long Copy for agent waits for the server's comments before copying what it has
const COMMENTS_WAIT_MS: number = 5000;

// The one obvious next step for a report in each status. A fix ships: fixed (in a pull request),
// then merged, then deployed; the status menu still reopens a fixed or merged one.
const NEXT_STEP: Record<FeedbackReportStatus, { label: string, to: FeedbackReportStatus }> = {
	new: { label: 'Approve', to: 'approved' },
	triaged: { label: 'Approve', to: 'approved' },
	needs_info: { label: 'Approve', to: 'approved' },
	approved: { label: 'Start work', to: 'progress' },
	progress: { label: 'Mark fixed', to: 'fixed' },
	fixed: { label: 'Mark merged', to: 'merged' },
	merged: { label: 'Mark deployed', to: 'deployed' },
	deployed: { label: 'Reopen', to: 'triaged' },
	wontfix: { label: 'Reopen', to: 'triaged' },
	duplicate: { label: 'Reopen', to: 'triaged' },
};

/**
 * One feedback report on the Feedback admin page: what the tester sent (their words, screenshots,
 * the board's recent activity, their device), and its triage. A report a developer's agent filed
 * or imported from a list has no board: it shows who filed it and what the list says instead.
 * Each change writes the report and, for a status change, a line in its comments, in one batch,
 * as firestore.rules shapes them
 * (./feedbackReports.ts builds both). "Working on it" claims the report, lets it go, takes it over,
 * or assigns it to another system admin (FeedbackAssignDialog), each in a transaction on the report
 * as it is. "Copy for agent" puts the report on the clipboard as Markdown, links included, for a
 * developer's agent session.
 */
export default Vue.extend({
	name: 'FeedbackReportDetail',
	props: {
		report: { type: Object as PropType<FeedbackReport>, required: true },
		// every loaded report, for the duplicate picker
		reports: { type: Array as PropType<FeedbackReport[]>, default: () => [] },
	},
	data () {
		return {
			shotUrls: [] as string[],
			shotsLoading: true,
			// the screenshot open full size, or null
			viewing: null as number | null,
			// the status waiting on its note (needs_info, wontfix, duplicate), or null
			statusDialog: null as FeedbackReportStatus | null,
			// the status the report had when that dialog opened. The change is made from it, so the
			// rules refuse it if someone else has changed the status since, and the dialog says so.
			statusDialogFrom: null as FeedbackReportStatus | null,
			// remounts the status menu, so one whose change was cancelled shows the saved status
			statusResets: 0,
			comments: [] as FeedbackReportComment[],
			commentsLoading: true,
			commentsError: false,
			// settles once the comments have loaded from the server, or failed to: Copy for agent waits
			// for it. The first snapshot can come from the local cache, and miss what changed since.
			commentsSettled: null as Promise<void> | null,
			commentsFromCache: true,
			names: {} as Record<string, string>,
			saving: null as string | null,
			copying: false,
			prDraft: '',
			duplicateDraft: null as number | null,
			// what taking the report over from whoever holds it leads to, while the confirmation is open:
			// claiming it, claiming it and starting work on it, or assigning it to `assignee`
			takeover: null as 'claim' | 'progress' | 'assign' | null,
			// who held it as the confirmation opened: confirming takes it from them, and nobody else
			takeoverFrom: null as ReportHolder | null,
			// the admin picker is open
			assigning: false,
			// the admin the report is being assigned to, while the take-over confirmation is about them
			assignee: null as FeedbackAdmin | null,
			// counts the duplicate field's saves: one whose lookup comes back after a later one began gives way
			duplicateSaves: 0,
			unsubscribe: null as (() => void) | null,
			kindLabels: KIND_LABELS,
			originLabels: ORIGIN_LABELS,
			statusLabels: STATUS_LABELS,
			priorityLabels: PRIORITY_LABELS,
			statusItems: FEEDBACK_REPORT_STATUSES.map((value: FeedbackReportStatus) => ({ text: STATUS_LABELS[value], value })),
			priorityItems: [
				{ text: 'No priority', value: null },
				...FEEDBACK_REPORT_PRIORITIES.map((value: FeedbackReportPriority) => ({ text: PRIORITY_LABELS[value], value })),
			],
		};
	},
	computed: {
		...mapState([
			'user',
			'authUser',
		]),
		...mapGetters([
			'displayLabels',
		]),
		isAdmin (): boolean {
			return isAdminReport(this.report);
		},
		// The note left with the change into the current status (the question, the reason), if any.
		statusNote (): FeedbackReportComment | null {
			for (let i = this.comments.length - 1; i >= 0; i--) {
				const comment: FeedbackReportComment = this.comments[i];
				if (comment.kind !== 'status') continue;
				return comment.to === this.report.status && comment.body ? comment : null;
			}
			return null;
		},
		statusNoteTitle (): string {
			if (this.report.status === 'needs_info') return 'Waiting on an answer';
			if (this.report.status === 'wontfix') return 'Closed as won\'t fix';
			if (this.report.status === 'duplicate') return `Closed as a duplicate of #${this.report.triage?.duplicateOf ?? '?'}`;
			if (this.report.status === 'merged') return 'Merged';
			if (this.report.status === 'deployed') return 'Deployed';
			return `Moved to ${STATUS_LABELS[this.report.status]}`;
		},
		statusNoteIcon (): string {
			if (this.report.status === 'needs_info') return 'mdi-help-circle-outline';
			if (this.report.status === 'duplicate') return 'mdi-content-copy';
			if (this.report.status === 'merged') return 'mdi-source-merge';
			if (this.report.status === 'deployed') return 'mdi-rocket-launch-outline';
			return 'mdi-information-outline';
		},
		questions (): { happened: string, expected: string } {
			return KIND_QUESTIONS[this.report.kind];
		},
		priority (): FeedbackReportPriority | null {
			return this.report.triage?.priority || null;
		},
		nextStep (): { label: string, to: FeedbackReportStatus } {
			// a question asked while it was approved work goes back to work once answered
			if (askedDuringWork(this.report, this.comments)) return { label: 'Back to approved', to: 'approved' };
			return NEXT_STEP[this.report.status];
		},
		// Who is working on it: a person, or a developer's agent
		holder (): ReportHolder | null {
			return currentHolder(this.report);
		},
		// This person holds it themself (not their agent)
		isMine (): boolean {
			return !!this.holder && !!this.user && this.holder.uid === this.user.id && this.holder.kind === 'human';
		},
		holderName (): string {
			if (!this.holder) return '';
			if (this.isMine) return 'You';
			if (this.holder.kind === 'agent' && this.user && this.holder.uid === this.user.id) {
				return this.$vuetify.breakpoint.smAndUp ? `Your agent (${this.holder.agent || 'Agent'})` : `Your ${this.holder.agent || 'agent'}`;
			}
			return this.holder.name || this.nameOf(this.holder.uid);
		},
		// Who assigned it to its holder, when somebody else did
		assignedByName (): string {
			return this.holder?.assignedBy ? this.nameOf(this.holder.assignedBy) : '';
		},
		holderTitle (): string {
			if (!this.holder) return '';
			const name: string = this.holder.name || this.holderName;
			return this.holder.assignedBy ? `${name}, assigned by ${this.assignedByName}` : name;
		},
		claimLabel (): string {
			if (isFeedbackReportClosed(this.report.status)) return '';
			if (!this.holder) return 'Claim it';
			return this.isMine ? 'Let it go' : 'Take over';
		},
		// Any admin assigns an open report to any system admin (a closed one has nobody on it)
		canAssign (): boolean {
			return !isFeedbackReportClosed(this.report.status);
		},
		// Whoever the take-over confirmation is about
		takeoverHolder (): ReportHolder | null {
			return this.takeover ? this.takeoverFrom : null;
		},
		takeoverName (): string {
			return this.takeoverHolder ? this.takeoverHolder.name || this.nameOf(this.takeoverHolder.uid) : '';
		},
		takeoverTitle (): string {
			if (this.takeover === 'assign' && this.assignee) return `Assign #${this.report.number} to ${this.assignee.name}?`;
			return `Take over #${this.report.number}?`;
		},
		// What confirming does, to the holder the confirmation names: an agent whose own developer
		// becomes the holder keeps working on it
		takeoverOutcome (): string {
			if (!this.takeover || !this.takeoverHolder) return '';
			return takeoverOutcomeText(this.takeover, this.takeoverHolder, this.user.id, this.assignee);
		},
		// "4 minutes", "an hour"
		takeoverFor (): string {
			return this.takeoverHolder?.since ? moment(this.takeoverHolder.since).fromNow(true) : '';
		},
		sentAt (): Date | null {
			return this.report.createdAt ? this.report.createdAt.toDate() : null;
		},
		// "sent ... from the board's Feedback button", "filed ...", "imported ... from Boards v2 sheet"
		arrival (): { verb: string, via: string } {
			return arrival(this.report);
		},
		// filed by a developer's agent, or imported from a list; null for a tester's report from a board
		origin (): FeedbackReportOriginKind | null {
			return originKind(this.report);
		},
		// what the list says of an imported report, and who filed it; empty for a board's report
		originFacts (): Partial<FeedbackReportOrigin> {
			return this.report.origin || {};
		},
		// the developer's agent that filed or imported it, as the page names it
		filedBy (): string {
			return this.report.origin?.name || 'A developer\'s agent';
		},
		originLink (): string | null {
			return httpLink(this.report.origin?.link);
		},
		// what it knows of its board, build and browser: one an agent filed or imported has an empty
		// placeholder, or null, for what it doesn't
		boardKnown (): boolean {
			return hasBoard(this.report.board);
		},
		buildKnown (): boolean {
			return hasBuild(this.report.build);
		},
		envKnown (): boolean {
			return hasEnv(this.report.env);
		},
		screenKnown (): boolean {
			return hasViewport(this.report.env);
		},
		// a report an agent filed with no board, build or browser has no context to show
		hasContext (): boolean {
			return !this.origin || this.boardKnown || this.buildKnown || this.envKnown;
		},
		boardHref (): string {
			const board: FeedbackReportBoard | null = this.report.board;
			return hasBoard(board) ? this.$router.resolve({ name: ROUTER.BOARD, params: { boardId: board.id } }).href : '';
		},
		reportRef (): DocumentReference {
			return db.collection('feedbackReports').doc(this.report.id);
		},
		author (): { uid: string, name: string } {
			return { uid: this.user.id, name: this.authUser?.displayName || this.user.email || 'Admin' };
		},
		// The people Copy for agent names by their id (who assigned it): you by your name, others by email
		agentNames (): Record<string, string> {
			return { ...this.names, [this.user.id]: this.author.name };
		},
		prRule (): (value: string) => boolean | string {
			return (value: string): boolean | string => !(value || '').trim() || isPullRequestUrl(value) || 'A link that starts with https://';
		},
	},
	watch: {
		'report.triage.pr': {
			immediate: true,
			handler (pr: string | null) {
				this.prDraft = pr || '';
			},
		},
		'report.triage.duplicateOf': {
			immediate: true,
			handler (duplicateOf: number | null) {
				this.duplicateDraft = duplicateOf ?? null;
			},
		},
		'report.triage.owner': {
			immediate: true,
			handler (uid: string | null) {
				if (uid) this.loadName(uid);
			},
		},
		'report.triage.approvedBy': {
			immediate: true,
			handler (uid: string | null) {
				if (uid) this.loadName(uid);
			},
		},
		'report.claim.assignedBy': {
			immediate: true,
			handler (uid: string | null | undefined) {
				if (uid) this.loadName(uid);
			},
		},
	},
	created () {
		this.loadShots();
		let settle: () => void = () => {};
		this.commentsSettled = new Promise<void>((resolve) => { settle = resolve; });
		this.unsubscribe = this.reportRef.collection('comments')
			.orderBy('createdAt')
			.onSnapshot({ includeMetadataChanges: true }, (snap) => {
				// a comment just added has no server time yet: estimate it, so it sorts last and shows a time
				this.comments = snap.docs.map((doc) => ({ ...(doc.data({ serverTimestamps: 'estimate' }) as FeedbackReportComment), id: doc.id }));
				this.commentsLoading = false;
				this.commentsError = false;
				this.commentsFromCache = snap.metadata.fromCache;
				if (!snap.metadata.fromCache) settle();
			}, (e: Error) => {
				console.error('[feedback] comments failed to load', this.report.id, e);
				this.commentsLoading = false;
				this.commentsError = true;
				settle();
			});
	},
	beforeDestroy () {
		if (this.unsubscribe) this.unsubscribe();
		this.unsubscribe = null;
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		dateOf (timestamp: { toDate: () => Date } | null | undefined): Date | null {
			return timestamp ? timestamp.toDate() : null;
		},
		nameOf (uid: string): string {
			if (this.user && uid === this.user.id) return 'you';
			return this.names[uid] || uid;
		},
		async loadName (uid: string) {
			if (this.names[uid] || (this.user && uid === this.user.id)) return;
			try {
				const snap = await db.collection('users').doc(uid).get();
				if (snap.exists) this.$set(this.names, uid, (snap.data() as User).email || uid);
			} catch (e) {
				console.error('[feedback] a name failed to load', uid, e);
			}
		},
		async loadShots () {
			this.shotsLoading = true;
			this.shotUrls = await Promise.all(this.report.shots.map(async (shot) => {
				try {
					return await feedbackFileUrl(shot.path);
				} catch (e) {
					console.error('[feedback] a screenshot failed to load', shot.path, e);
					return '';
				}
			}));
			this.shotsLoading = false;
		},
		// One batch: the report, and a line in its comments when there is one. Resolves true once saved.
		async write (key: string, changes: FeedbackUpdate, comment: Record<string, unknown> | null, done: string): Promise<boolean> {
			this.saving = key;
			try {
				const batch = db.batch();
				batch.update(this.reportRef, changes);
				if (comment) batch.set(this.reportRef.collection('comments').doc(), comment);
				await batch.commit();
				this.updateSnackbar({ status: SNACKBAR_STATUS.SUCCESS, message: done, show: true });
				return true;
			} catch (e) {
				console.error('[feedback] failed to save', this.report.id, key, e);
				// the rules refuse a status change made from a status that has since changed
				const refused: boolean = (e as { code?: string })?.code === 'permission-denied';
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: refused
						? `Couldn't save #${this.report.number}. Someone may have just changed it: check it and try again.`
						: `Couldn't save #${this.report.number}, try again`,
					show: true,
				});
				return false;
			} finally {
				this.saving = null;
			}
		},
		async setStatus (to: FeedbackReportStatus) {
			if (!to || to === this.report.status || this.saving) return;
			// starting work claims it: in a transaction, which asks first when someone else holds it
			if (to === 'progress') {
				this.statusResets++;
				if (heldByOther(this.report, this.user.id)) this.openTakeover('progress');
				else await this.claimTransaction('progress', null);
				return;
			}
			if (statusNeedsNote(to)) {
				this.statusDialogFrom = this.report.status;
				this.statusDialog = to;
				return;
			}
			await this.saveStatus(to, this.report.status, '', null);
		},
		// `from` is the status the change was decided on: one someone else has changed since isn't
		// saved. (Starting work, which claims the report, goes through claimTransaction instead.)
		async saveStatus (to: FeedbackReportStatus, from: FeedbackReportStatus, note: string, duplicateOf: number | null): Promise<boolean> {
			if (from !== this.report.status || to === from) return false;
			const stamp = FieldValue.serverTimestamp();
			const changes: FeedbackUpdate = statusUpdate(this.report, to, this.user.id, stamp);
			if (to === 'duplicate') changes['triage.duplicateOf'] = duplicateOf;
			const saved: boolean = await this.write(
				'status',
				changes,
				statusComment(from, to, this.author, stamp, note),
				this.statusMessage(to, duplicateOf),
			);
			if (!saved) return false;
			this.statusDialog = null;
			this.statusDialogFrom = null;
			this.$emit('status-changed', to);
			return true;
		},
		statusMessage (to: FeedbackReportStatus, duplicateOf: number | null): string {
			const n: string = `#${this.report.number}`;
			if (to === 'approved') return `${n} approved`;
			if (to === 'duplicate') return `${n} closed as a duplicate of #${duplicateOf}`;
			if (isFeedbackReportClosed(this.report.status) && !isFeedbackReportClosed(to)) return `${n} reopened`;
			return `${n} is now ${STATUS_LABELS[to]}`;
		},
		closeStatusDialog () {
			this.statusDialog = null;
			this.statusDialogFrom = null;
			this.statusResets++;
		},
		onStatusConfirm ({ note, duplicateOf }: { note: string, duplicateOf: number | null }) {
			if (this.statusDialog && this.statusDialogFrom) this.saveStatus(this.statusDialog, this.statusDialogFrom, note, duplicateOf);
		},
		// The A key: approves a report waiting on review. Returns whether it did.
		approve (): boolean {
			if (this.nextStep.to !== 'approved' || this.saving) return false;
			this.setStatus('approved');
			return true;
		},
		// The C key.
		focusComment () {
			const comments = this.$refs.comments as (Vue & { focus: () => void }) | undefined;
			if (comments) comments.focus();
		},
		setPriority (priority: FeedbackReportPriority | null) {
			if ((priority || null) === this.priority) return;
			this.write(
				'priority',
				triageUpdate(this.report, { 'triage.priority': priority || null }, FieldValue.serverTimestamp()),
				null,
				priority ? `#${this.report.number} is ${PRIORITY_LABELS[priority]}` : `#${this.report.number} has no priority`,
			);
		},
		// Claim it, let it go, or (after confirming) take it over from whoever holds it
		claimAction () {
			if (this.saving) return;
			if (this.isMine) {
				this.write('claim', claimUpdate(this.report, null, FieldValue.serverTimestamp()), null, `You let go of #${this.report.number}`);
				return;
			}
			if (this.holder) {
				this.openTakeover('claim');
				return;
			}
			this.claimTransaction('claim', null);
		},
		// The confirmation is about whoever holds it as it opens: confirming takes it from them only
		// (and, assigning it, gives it to `assignee`)
		openTakeover (then: 'claim' | 'progress' | 'assign', assignee: FeedbackAdmin | null = null) {
			this.takeoverFrom = heldByOther(this.report, this.user.id);
			this.assignee = assignee;
			this.takeover = then;
		},
		async confirmTakeover () {
			const then: 'claim' | 'progress' | 'assign' | null = this.takeover;
			if (!then || this.saving) return;
			if (await this.claimTransaction(then, this.takeoverFrom, then === 'assign' ? this.assignee : null)) this.takeover = null;
		},
		// The picker's choice: another admin is assigned it; choosing yourself claims it.
		assignTo (admin: FeedbackAdmin) {
			if (this.saving) return;
			if (admin.isMe) {
				this.assigning = false;
				if (!this.isMine) this.claimAction();
				return;
			}
			this.claimTransaction('assign', null, admin);
		},
		// Claiming, starting work (which claims it), and assigning it to `assignee` (another admin), in
		// a transaction on the report as it is now. Someone else holding it who isn't `takingFrom` (the
		// holder the take-over confirmation named) writes nothing: the confirmation opens, about them.
		// A take-over, assigning over someone else's claim included, names the claim it replaces
		// (takeoverOf), as firestore.rules wants. Resolves true once saved.
		async claimTransaction (then: 'claim' | 'progress' | 'assign', takingFrom: ReportHolder | null, assignee: FeedbackAdmin | null = null): Promise<boolean> {
			const target: FeedbackAdmin | null = then === 'assign' ? assignee : null;
			if (then === 'assign' && !target) return false;
			this.saving = then === 'progress' ? 'status' : then;
			const from: FeedbackReportStatus = this.report.status;
			const n: string = `#${this.report.number}`;
			try {
				const outcome: 'saved' | 'held' | 'moved' | 'closed' | 'already' = await db.runTransaction(async (transaction) => {
					const snap = await transaction.get(this.reportRef);
					const fresh: FeedbackReport = { ...(snap.data() as FeedbackReport), id: snap.id };
					// closed meanwhile (a dialog confirmed after someone marked it fixed): nobody claims it
					if (claimBlocked(fresh, from) === 'closed') return 'closed';
					const holder: ReportHolder | null = currentHolder(fresh);
					// the admin it's being assigned to holds it already, as a person
					if (target && holder && holder.kind === 'human' && holder.uid === target.uid) return 'already';
					const other: ReportHolder | null = heldByOther(fresh, this.user.id);
					// held by anyone but the exact claim the confirmation named (who, device, session, since)
					if (other && !(takingFrom && other.key === takingFrom.key)) return 'held';
					if (claimBlocked(fresh, from) === 'moved') return 'moved';
					const mine: boolean = !!holder && holder.uid === this.user.id && holder.kind === 'human';
					const takeoverOf: unknown = other ? fresh.claim?.claimedAt ?? null : null;
					const stamp = FieldValue.serverTimestamp();
					if (target) {
						transaction.update(this.reportRef, assignUpdate(fresh, { uid: target.uid, name: target.name }, this.user.id, stamp, takeoverOf));
					} else if (then === 'claim') {
						if (!mine) transaction.update(this.reportRef, claimUpdate(fresh, this.author, stamp, takeoverOf));
					} else {
						transaction.update(this.reportRef, statusUpdate(fresh, 'progress', this.user.id, stamp, mine ? null : this.author, takeoverOf));
						transaction.set(this.reportRef.collection('comments').doc(), statusComment(from, 'progress', this.author, stamp));
					}
					return 'saved';
				});
				if (outcome === 'held') {
					this.assigning = false;
					this.openTakeover(then, target);
					return false;
				}
				if (outcome === 'moved') {
					this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: `${n} just changed. Check it and try again.`, show: true });
					return false;
				}
				if (outcome === 'closed') {
					// nothing to retry: close the dialog that asked
					this.assigning = false;
					this.takeover = null;
					this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: `${n} was closed meanwhile. Reopen it to work on it.`, show: true });
					return false;
				}
				this.assigning = false;
				if (outcome === 'already' && target) {
					this.updateSnackbar({ status: SNACKBAR_STATUS.INFO, message: `${target.name} is already working on ${n}`, show: true });
					return true;
				}
				let message: string = `${n} is yours`;
				if (then === 'progress') message = this.statusMessage('progress', null);
				else if (target) message = `Assigned ${n} to ${target.name}`;
				this.updateSnackbar({ status: SNACKBAR_STATUS.SUCCESS, message, show: true });
				if (then === 'progress') this.$emit('status-changed', 'progress');
				return true;
			} catch (e) {
				console.error('[feedback] failed to claim', this.report.id, then, e);
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: `Couldn't save #${this.report.number}, try again`, show: true });
				return false;
			} finally {
				this.saving = null;
			}
		},
		// Esc in a field: back to the page's shortcuts (leaving it saves it, as clicking away does)
		leaveField (e: KeyboardEvent) {
			if (e.target instanceof HTMLElement) e.target.blur();
		},
		openPr () {
			if (this.report.triage?.pr) window.open(this.report.triage.pr, '_blank', 'noopener');
		},
		savePr () {
			const pr: string = (this.prDraft || '').trim();
			if ((pr || null) === (this.report.triage?.pr || null)) return;
			if (pr && !isPullRequestUrl(pr)) return;
			this.write(
				'pr',
				triageUpdate(this.report, { 'triage.pr': pr || null }, FieldValue.serverTimestamp()),
				null,
				pr ? `Pull request saved on #${this.report.number}` : `Pull request removed from #${this.report.number}`,
			);
		},
		async saveDuplicateOf () {
			const n: number | null = Number.isInteger(this.duplicateDraft) && this.duplicateDraft > 0 && this.duplicateDraft !== this.report.number ? this.duplicateDraft : null;
			// counted before anything else, so even putting back the saved number cancels a save in flight
			const save: number = ++this.duplicateSaves;
			if (n === (this.report.triage?.duplicateOf ?? null)) return;
			const refuse = (message: string) => {
				this.duplicateDraft = this.report.triage?.duplicateOf ?? null;
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message, show: true });
			};
			// a duplicate keeps the report it repeats: reopen it to clear that
			if (n === null) return refuse('A duplicate needs the number of the report it repeats');
			if (!this.reports.some((report: FeedbackReport) => report.number === n)) {
				let found: FeedbackReport | null | undefined;
				try {
					found = await findReportByNumber(n);
				} catch (e) {
					console.error('[feedback] failed to look up report', n, e);
				}
				// the number was changed again while this one was looked up: the later save decides
				if (save !== this.duplicateSaves) return;
				if (found === undefined) return refuse(`Couldn't look up #${n}, try again`);
				if (!found) return refuse(`There's no report #${n}`);
			}
			await this.write(
				'duplicateOf',
				triageUpdate(this.report, { 'triage.duplicateOf': n }, FieldValue.serverTimestamp()),
				null,
				n ? `#${this.report.number} repeats #${n}` : `#${this.report.number} repeats no report`,
			);
		},
		async copyLink () {
			const href: string = this.$router.resolve({ name: ROUTER.ADMIN_FEEDBACK, params: { number: String(this.report.number) } }).href;
			try {
				await copyText(window.location.origin + href);
				this.updateSnackbar({ status: SNACKBAR_STATUS.SUCCESS, message: `Copied a link to #${this.report.number}`, show: true });
			} catch (e) {
				console.error('[feedback] failed to copy the link', e);
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: 'Couldn\'t copy the link, try again', show: true });
			}
		},
		// The report as Markdown, with links to its files that work for an hour (getFeedbackFileLinks),
		// once its comments have loaded. Without links, the files' storage paths stand in.
		async copyForAgent () {
			this.copying = true;
			try {
				// the comments as the server has them, or as they are after a few seconds' wait
				if (this.commentsSettled) await Promise.race([this.commentsSettled, new Promise((resolve) => window.setTimeout(resolve, COMMENTS_WAIT_MS))]);
				let links: AgentReportLinks = { shots: [], bundle: null, expiresAt: null };
				let linksMessage: string = '';
				try {
					const result = await functions.httpsCallable('getFeedbackFileLinks')({ reportId: this.report.id });
					const answer: FeedbackFileLinksResult = result.data as FeedbackFileLinksResult;
					if (answer.success === true) links = { shots: answer.shots, bundle: answer.bundle, expiresAt: answer.expiresAt };
					else linksMessage = answer.message;
				} catch (e) {
					console.error('[feedback] the links could not be made', this.report.id, e);
					linksMessage = 'The links could not be made.';
				}
				// what was copied: the comments can arrive while the clipboard is written
				const text: string = reportForAgent(this.report, this.comments, links, this.agentNames);
				const copied = copiedMessage(this.report.number, { links: linksMessage, commentsFailed: this.commentsError, commentsCached: this.commentsFromCache });
				await copyText(text);
				this.updateSnackbar({
					status: copied.ok ? SNACKBAR_STATUS.SUCCESS : (copied.warnOnly ? SNACKBAR_STATUS.WARN : SNACKBAR_STATUS.ERROR),
					message: copied.message,
					show: true,
				});
			} catch (e) {
				console.error('[feedback] failed to copy', this.report.id, e);
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: 'Couldn\'t copy the report, try again', show: true });
			} finally {
				this.copying = false;
			}
		},
	},
	components: {
		FeedbackActivity,
		FeedbackAssignDialog,
		FeedbackChip,
		FeedbackComments,
		FeedbackShotViewer,
		FeedbackStatusDialog,
		ConfirmDialog,
		NumberField,
	},
});
